import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useReducer } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import postProductVatPluId from '../../../api/oc_product/bulk/bulk_process/product_vat/plu_id/posts/postProductVatPluId';
import postProductVatProductId from '../../../api/oc_product/bulk/bulk_process/product_vat/product_id/posts/postProductVatProductId';
import getSources from '../../../api/oc_product/sites/sources/gets/getSources';
import VirtualizedAutocomplete from '../../../components/VirtualizedAutocomplete';
import doesObjectContainEmptyValues from '../../../utils/doesObjectContainEmptyValues';

const Input = styled('input')({ display: 'none' });

const PluCsv = '/bulk_templates/Bulk_Plu_Template.csv';
const ProductIdCsv = '/bulk_templates/Bulk_Product_Id_Template.csv';

const MAX_FILE_SIZE_MB = 5;

const reducer = (state, action) => {
  switch (action.type) {
    case 'SOURCE':
      return {
        ...state,
        sourceId: action.payload,
      };
    case 'FILE':
      return {
        ...state,
        file: action.payload,
      };
    case 'RESET':
      return action.payload;
    default:
      return state;
  }
};

function VAT({ refreshReports }) {
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const fileInputRef = React.useRef(null);

  const initialState = {
    sourceId: null,
    file: '',
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [idType, setIdType] = React.useState('plu');
  const [submitDisabled, setSubmitDisabled] = React.useState(true);
  const submitBtn = React.useRef(null);
  const [buttonWidth, setButtonWidth] = React.useState(0);
  const [buttonHeight, setButtonHeight] = React.useState(0);
  const [sourceOptions, setSourceOptions] = React.useState([]);

  const handleSave = (e) => {
    e.preventDefault();
    if (idType === 'plu') {
      postProductVatPluId({
        SiteId: siteId,
        SourceId: state.sourceId,
        File: state.file,
      })
        .then(() => refreshReports(true))
        .catch((err) => console.error(err));
    } else if (idType === 'productid') {
      postProductVatProductId({
        SiteId: siteId,
        File: state.file,
      })
        .then(() => refreshReports(true))
        .catch((err) => console.error(err));
    }
  };

  React.useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input value
      dispatch({ type: 'FILE', payload: '' }); // Reset file state
      dispatch({ type: 'RESET', payload: initialState });
    }
    setIdType('plu');
    getSources(siteId)
      .then((res) => setSourceOptions(res.data))
      .catch((err) => console.error(err));
    dispatch({ type: 'RESET', payload: initialState });
  }, [siteId]);

  React.useEffect(() => {
    if (submitBtn.current) {
      const sbmBtn = submitBtn.current.getBoundingClientRect();
      setButtonWidth(sbmBtn.width);
      setButtonHeight(sbmBtn.height);
    }
  }, [submitBtn.current]);

  React.useEffect(() => {
    if (idType !== 'plu') {
      delete state.sourceId;
    } else if (!state.sourceId) {
      state.sourceId = '';
    }
    const isEmpty = doesObjectContainEmptyValues(state);
    setSubmitDisabled(isEmpty);
  }, [state, idType]);

  React.useEffect(() => {
    if (state.file !== '' && state.file.type !== 'text/csv' && state.file.type !== 'application/vnd.ms-excel') {
      toast.error('Please upload CSV file');
    }

    if (state.file.type !== 'text/csv' && state.file.type !== 'application/vnd.ms-excel') {
      const isEmpty = doesObjectContainEmptyValues(state);
      setSubmitDisabled(isEmpty);
      state.file = '';
    }

    if (state.file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error('File size is too big. Please upload a file smaller than 5MB.');
      const isEmpty = doesObjectContainEmptyValues(state);
      setSubmitDisabled(isEmpty);
      state.file = '';
    }
  }, [state.file]);

  return (
    <div className="flex flex-col items-start justify-start w-full gap-6 mt-4 xl:flex-row">
      <div className="flex flex-col">
        <div
          className="mb-8 bg-white border rounded-lg right-8"
        >
          <div className="flex flex-col h-full p-4 w-96">
            <span className="text-lg">Download Templates</span>
            <a
              href={PluCsv}
              download="Bulk_Plu_Template.csv"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-700"
            >
              Plu Template
            </a>
            <a
              href={ProductIdCsv}
              download="Bulk_Product_Id_Template.csv"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-700"
            >
              Prod Id Template
            </a>
          </div>
        </div>
        <section>
          <div className="flex-1 p-4 mb-6 text-left bg-white border-2 rounded w-96 border-error-600">
            <Typography gutterBottom className="font-gothammedium">
              Important
            </Typography>
            <ul>
              <li className="pt-2">
                The VAT value for each product should be either True (if it is VATable) or False (if it is not VATable).
              </li>
              <li className="pt-2">Once the CSV file is uploaded, all SKUs of the product will be assigned the same VAT value.</li>
            </ul>
          </div>
        </section>
      </div>

      <Container maxWidth="md">
        <section className="w-full">
          <form className="flex h-[calc(100vh-185px)] flex-col flex-wrap gap-8" onSubmit={handleSave}>
            <section className="flex flex-col max-w-sm gap-8">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-4 h-4 p-3 text-white rounded-full bg-secondary-500">
                    <Typography variant="caption" className="font-gothammedium">
                      1
                    </Typography>
                  </div>
                  <Typography variant="body2">
                    Select PLU and Source or Product ID
                  </Typography>
                </div>
                <Box className="flex justify-start">
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      aria-label="type"
                      defaultValue="plu"
                      name="radio-buttons-group"
                      onChange={(e) => setIdType(e.target.value)}
                      value={idType}
                      sx={{ mb: 2 }}
                    >
                      <FormControlLabel value="plu" control={<Radio />} label="PLU" />
                      <FormControlLabel
                        value="productid"
                        control={<Radio />}
                        label="Product ID"
                      />
                    </RadioGroup>
                    {idType === 'plu' ? (
                      <VirtualizedAutocomplete
                        options={sourceOptions}
                        textFieldLabel="Sources"
                        textFieldVariant="outlined"
                        value={sourceOptions.find((obj) => obj?.SourceId === state.sourceId) || null}
                        getOptionLabel={(option) => option.Name}
                        onSelectedValue={(value) => dispatch({ type: 'SOURCE', payload: value?.SourceId })}
                        optionKey="Name"
                      />
                    ) : (
                      <div className="hidden" />
                    )}
                  </FormControl>
                </Box>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-4 h-4 p-3 text-white rounded-full bg-secondary-500">
                    <Typography variant="caption" className="font-gothammedium">
                      2
                    </Typography>
                  </div>
                  <Typography variant="body2">
                    Upload File
                  </Typography>
                </div>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    alignItems: 'center',
                  }}
                >
                  <label htmlFor="contained-button-file">
                    <Input
                      accept=".csv"
                      id="contained-button-file"
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        if (e.target.files.length > 0) {
                          dispatch({ type: 'FILE', payload: e.target.files[0] });
                        }
                      }}
                    />
                    <Button disabled={idType === 'plu' && !state.sourceId} variant="outlined" component="span" color="info">
                      Choose File
                    </Button>
                  </label>
                  <TextField
                    disabled
                    value={state.file?.name || ''}
                    variant="standard"
                    placeholder="No File Chosen"
                  />
                  {state.file && (
                    <CancelIcon
                      className="cursor-pointer text-error-400"
                      onClick={() => {
                        dispatch({ type: 'FILE', payload: '' });
                        fileInputRef.current.value = '';
                      }}
                    />
                  )}
                </Box>

                <div className="w-full">
                  <div className="relative float-right w-max">
                    <Button variant="contained" ref={submitBtn} className="z-10" disabled={submitDisabled} type="submit">
                      Submit
                    </Button>
                    {submitDisabled ? null : (
                      <div
                        className="absolute left-0 right-0 z-0 ml-auto mr-auto rounded top-1 bg-primary-400 animate-ping"
                        style={{ width: `${buttonWidth / 1.5}px`, height: `${buttonHeight / 1.25}px` }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>
          </form>
        </section>
      </Container>
    </div>
  );
}

export default VAT;
